import { Fragment, useRef, RefObject } from "react";
import SEO from "../components/SEO";
import { Link } from "react-router-dom";
import {
  ArrowLeftCircleIcon,
  ArrowRightCircleIcon,
  BoltIcon,
  DevicePhoneMobileIcon,
  LockClosedIcon,
  CheckCircleIcon,
  CreditCardIcon,
  UserPlusIcon,
  BuildingLibraryIcon,
  ArrowRightIcon,
} from "@heroicons/react/24/outline";
import { CheckCircleIcon as CheckCircleSolid } from "@heroicons/react/24/solid";
import payment from "../assets/payment.svg";
import cm1 from "../assets/escolas/cat.jpg";
import cm2 from "../assets/escolas/osbrilhates.png";
import cm3 from "../assets/escolas/martires.png";
import cm4 from "../assets/escolas/girasol.png";
import cm5 from "../assets/escolas/monatower.png";
import cm6 from "../assets/escolas/pitruca.png";
import um1 from "../assets/escolas/catolica.jpg";
import um2 from "../assets/escolas/imetro.png";
import um3 from "../assets/escolas/isptec.png";
import um4 from "../assets/escolas/oscaricas.png";
import um5 from "../assets/escolas/unibelas.png";

const schoolLogos = [cm1, cm2, cm3, cm4, cm5, cm6];
const uniLogos    = [um1, um2, um3, um4, um5];

const steps = [
  { Icon: UserPlusIcon,    num: 1, title: "Crie a sua conta",     desc: "Registe-se em menos de 2 minutos, totalmente gratuito e sem burocracia." },
  { Icon: CreditCardIcon,  num: 2, title: "Escolha o serviço",    desc: "Propina, matrícula, uniforme ou qualquer outro serviço da sua instituição." },
  { Icon: CheckCircleIcon, num: 3, title: "Pague em segundos",    desc: "Confirmação imediata para ambas as partes. Recibo digital gerado automaticamente." },
];

const features = [
  { Icon: BoltIcon,              bg: "bg-amber-100",   fg: "text-amber-600",   title: "Pagamento Instantâneo",  desc: "Processamento em segundos com confirmação em tempo real, 24 horas por dia." },
  { Icon: DevicePhoneMobileIcon, bg: "bg-violet-100",  fg: "text-violet-600",  title: "Qualquer Dispositivo",   desc: "Telemóvel, computador ou Smart TV — onde quiser, quando precisar." },
  { Icon: LockClosedIcon,        bg: "bg-emerald-100", fg: "text-emerald-600", title: "Segurança Bancária",     desc: "Encriptação de ponta a ponta e autenticação em dois factores em cada transação." },
  { Icon: CheckCircleIcon,       bg: "bg-blue-100",    fg: "text-blue-600",    title: "Sem Filas, Sem Papel",   desc: "Elimine deslocações ao banco. Recibo digital imediato para o encarregado." },
];

function scrollCarousel(ref: RefObject<HTMLDivElement>, dir: "left" | "right") {
  if (!ref.current) return;
  ref.current.scrollLeft += dir === "left"
    ? -ref.current.offsetWidth * 0.75
    :  ref.current.offsetWidth * 0.75;
}

function Carousel({ title, logos, refEl, onLeft, onRight }: {
  title: string; logos: string[]; refEl: RefObject<HTMLDivElement>;
  onLeft: () => void; onRight: () => void;
}) {
  return (
    <div className="mb-12">
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-bold text-dark text-base">{title}</h3>
        <div className="flex gap-2">
          <button onClick={onLeft}  className="w-9 h-9 rounded-full border border-border bg-white flex items-center justify-center hover:bg-brand hover:border-brand group transition-all">
            <ArrowLeftCircleIcon  className="w-5 h-5 text-muted group-hover:text-white transition-colors" />
          </button>
          <button onClick={onRight} className="w-9 h-9 rounded-full border border-border bg-white flex items-center justify-center hover:bg-brand hover:border-brand group transition-all">
            <ArrowRightCircleIcon className="w-5 h-5 text-muted group-hover:text-white transition-colors" />
          </button>
        </div>
      </div>
      <div ref={refEl} className="no-scrollbar flex gap-4 overflow-x-auto scroll-smooth py-1">
        {logos.map((src, i) => (
          <div key={i} className="shrink-0 w-20 h-20 rounded-2xl overflow-hidden border border-border bg-white shadow-sm hover:shadow-md hover:scale-105 transition-all cursor-pointer">
            <img src={src} alt="" className="w-full h-full object-cover" />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Pagementos() {
  const refSchool = useRef<HTMLDivElement>(null);
  const refUni    = useRef<HTMLDivElement>(null);

  return (
    <Fragment>
      <SEO
        title="Pagamentos Escolares Online em Angola"
        description="Pague propinas, matrículas e serviços escolares online sem filas e sem deslocações. Confirmação imediata, do telemóvel ou computador, em qualquer parte de Angola."
        path="/payment"
        schema={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Pagamentos Escolares Online — PIA",
          "url": "https://pia-payment.vercel.app/payment",
          "description": "Pague propinas e matrículas online em Angola.",
          "breadcrumb": { "@type": "BreadcrumbList", "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Início", "item": "https://pia-payment.vercel.app/" },
            { "@type": "ListItem", "position": 2, "name": "Pagamentos", "item": "https://pia-payment.vercel.app/payment" }
          ]},
        }}
      />

      {/* ══════════════════════════════════════════
          HERO — gradient com elementos decorativos
      ══════════════════════════════════════════ */}
      <section
        className="relative py-28"
        style={{ background: "linear-gradient(135deg, #001627 0%, #002849 45%, #0569a3 100%)" }}
      >
        {/* Decorative layer — overflow-hidden aqui para não clipar a wave */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Dot grid overlay */}
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.06) 1px, transparent 1px)",
              backgroundSize: "28px 28px",
            }}
          />
          {/* Glow circles */}
          <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full" style={{ background: "radial-gradient(circle, rgba(43,171,227,0.25) 0%, transparent 70%)" }} />
          <div className="absolute -bottom-20 -left-10 w-72 h-72 rounded-full" style={{ background: "radial-gradient(circle, rgba(5,105,163,0.3) 0%, transparent 70%)" }} />
        </div>

        <div className="relative max-w-6xl mx-auto px-6 flex items-center gap-14 max-md:flex-col">

          {/* Left */}
          <div className="flex-1 max-w-xl">
            {/* Live badge */}
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/15 backdrop-blur-sm text-white/80 text-xs font-medium px-4 py-1.5 rounded-full mb-8">
              <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
              Pagamentos em tempo real · Angola
            </div>

            <h1 className="text-5xl font-extrabold text-white leading-[1.1] mb-5 max-md:text-3xl">
              Pague propinas{" "}
              <span
                className="text-transparent bg-clip-text"
                style={{ backgroundImage: "linear-gradient(90deg, #2babe3, #7dd3fc)" }}
              >
                sem sair
              </span>{" "}
              de casa
            </h1>

            <p className="text-white/60 text-base leading-relaxed mb-8 max-w-md">
              Sem filas, sem deslocações. Pague matrícula, uniforme, cartão de
              estudante e muito mais — do telemóvel ou computador.
            </p>

            <div className="flex flex-wrap gap-4 mb-10">
              <Link
                to="/register-user/1"
                className="inline-flex items-center gap-2 text-white font-semibold px-7 py-3.5 rounded-full text-sm transition-all hover:opacity-90"
                style={{ background: "linear-gradient(90deg, #2babe3, #016fb9)" }}
              >
                Cadastre-se Grátis
                <ArrowRightIcon className="w-4 h-4" />
              </Link>
              <a
                href="#como-funciona"
                className="inline-block border border-white/20 hover:bg-white/10 text-white/80 hover:text-white font-medium px-7 py-3.5 rounded-full text-sm transition-all"
              >
                Como funciona?
              </a>
            </div>

            {/* Floating success card */}
            <div className="inline-flex items-center gap-3.5 bg-white/10 backdrop-blur-md border border-white/15 rounded-2xl px-5 py-4 shadow-2xl">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: "linear-gradient(135deg, #10b981, #059669)" }}>
                <CheckCircleSolid className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-white text-sm font-bold leading-tight">Propina paga com sucesso!</p>
                <p className="text-white/50 text-xs mt-0.5">Colégio Os Brilhantes · 25.000 Kz · agora</p>
              </div>
            </div>
          </div>

          {/* Right — illustration */}
          <div className="flex-1 flex justify-center items-center relative max-md:hidden">
            {/* Glow behind image */}
            <div className="absolute w-72 h-72 rounded-full" style={{ background: "radial-gradient(circle, rgba(43,171,227,0.2) 0%, transparent 70%)" }} />
            <img src={payment} alt="Pagamentos PIA" className="relative w-full max-w-xs drop-shadow-2xl" />
          </div>
        </div>

        {/* Wave bottom */}
        <div className="absolute bottom-0 left-0 right-0" style={{ lineHeight: 0 }}>
          <svg viewBox="0 0 1440 60" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none"
            style={{ display: "block", width: "100%", height: 60 }}>
            <path d="M0,60 L0,30 C180,60 360,0 540,20 C720,40 900,0 1080,20 C1260,40 1350,10 1440,30 L1440,60 Z" fill="#f8fafc" />
          </svg>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          COMO FUNCIONA — 3 passos
      ══════════════════════════════════════════ */}
      <section id="como-funciona" className="py-20 bg-off-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-blue-light font-semibold text-xs uppercase tracking-widest">Simples e Rápido</span>
            <h2 className="text-3xl font-extrabold text-dark mt-2">Como funciona?</h2>
            <p className="text-muted text-sm mt-3">3 passos para o seu primeiro pagamento</p>
          </div>

          <div className="grid grid-cols-3 gap-8 max-md:grid-cols-1">
            {steps.map(({ Icon, num, title, desc }, i) => (
              <div key={num} className="relative flex flex-col items-center text-center group">
                {/* Connector line */}
                {i < steps.length - 1 && (
                  <div className="hidden md:block absolute top-10 left-[calc(50%+56px)] right-0 h-px bg-linear-to-r from-border to-transparent" />
                )}
                {/* Icon circle */}
                <div className="relative mb-6">
                  <div
                    className="w-20 h-20 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform"
                    style={{ background: "linear-gradient(135deg, #002849, #0569a3)" }}
                  >
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <span
                    className="absolute -top-2 -right-2 w-7 h-7 rounded-full text-xs font-extrabold text-white flex items-center justify-center shadow-md"
                    style={{ background: "linear-gradient(135deg, #2babe3, #016fb9)" }}
                  >
                    {num}
                  </span>
                </div>
                <h3 className="font-bold text-dark text-base mb-2">{title}</h3>
                <p className="text-muted text-sm leading-relaxed max-w-52">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          VANTAGENS — 2×2 cards coloridos
      ══════════════════════════════════════════ */}
      <section className="py-20 bg-sky">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="text-blue-light font-semibold text-xs uppercase tracking-widest">Benefícios</span>
            <h2 className="text-3xl font-extrabold text-dark mt-2">Por que escolher o PIA?</h2>
          </div>

          <div className="grid grid-cols-2 gap-5 max-md:grid-cols-1">
            {features.map(({ Icon, bg, fg, title, desc }) => (
              <div
                key={title}
                className="bg-white rounded-2xl p-7 border border-border flex items-start gap-5 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group"
              >
                <div className={`w-13 h-13 rounded-xl flex items-center justify-center shrink-0 ${bg} group-hover:scale-110 transition-transform`}>
                  <Icon className={`w-6 h-6 ${fg}`} />
                </div>
                <div>
                  <h3 className="font-bold text-dark text-sm mb-1.5">{title}</h3>
                  <p className="text-muted text-sm leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          INSTITUIÇÕES — carrossel
      ══════════════════════════════════════════ */}
      <section className="py-20 bg-off-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-blue-light font-semibold text-xs uppercase tracking-widest">Parceiros</span>
            <h2 className="text-3xl font-extrabold text-dark mt-2">Encontre a sua instituição</h2>
            <p className="text-muted text-sm mt-3">Colégios e universidades já integrados na plataforma</p>
          </div>
          <Carousel title="Colégios"     logos={schoolLogos} refEl={refSchool}
            onLeft={() => scrollCarousel(refSchool, "left")}  onRight={() => scrollCarousel(refSchool, "right")} />
          <Carousel title="Universidades" logos={uniLogos}    refEl={refUni}
            onLeft={() => scrollCarousel(refUni, "left")}     onRight={() => scrollCarousel(refUni, "right")} />
        </div>
      </section>

      {/* ══════════════════════════════════════════
          CTA — gradient final
      ══════════════════════════════════════════ */}
      <section
        className="relative py-24 overflow-hidden"
        style={{ background: "linear-gradient(135deg, #001627 0%, #002849 40%, #0077b6 100%)" }}
      >
        {/* Dot grid */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.05) 1px, transparent 1px)",
            backgroundSize: "28px 28px",
          }}
        />
        {/* Glow circles */}
        <div className="absolute top-0 right-0 w-80 h-80 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(43,171,227,0.2) 0%, transparent 70%)" }} />
        <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(5,105,163,0.25) 0%, transparent 70%)" }} />

        <div className="relative max-w-2xl mx-auto px-6 text-center">
          <div className="w-16 h-16 rounded-2xl mx-auto mb-8 flex items-center justify-center" style={{ background: "linear-gradient(135deg, #2babe3, #016fb9)" }}>
            <BuildingLibraryIcon className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-4xl font-extrabold text-white mb-4">Pronto para começar?</h2>
          <p className="text-white/60 text-base mb-10 leading-relaxed">
            Junte-se a centenas de instituições angolanas que já confiam no PIA
            para gerir os seus pagamentos.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link
              to="/register-user/1"
              className="inline-flex items-center gap-2 bg-white hover:bg-off-white text-brand font-bold px-8 py-4 rounded-full text-sm transition-all hover:shadow-lg hover:scale-105"
            >
              Criar Conta Gratuita
              <ArrowRightIcon className="w-4 h-4" />
            </Link>
            <a
              href="#como-funciona"
              className="inline-block border border-white/25 hover:bg-white/10 text-white/80 hover:text-white font-medium px-8 py-4 rounded-full text-sm transition-all"
            >
              Saiba mais
            </a>
          </div>
        </div>
      </section>

    </Fragment>
  );
}
